import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerSwipeZone,
    DrawerTitle,
} from '@rocco/components/drawer';
import { Image } from '@rocco/components/image';
import { useUserLoggedIn } from '@modules/user';
import {
    EMAIL_INPUT,
    FIRST_NAME_INPUT,
    NAME_INPUT,
    PHONE_NUMBER_INPUT,
} from '@modules/validation/util/form';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import { useEnquirySession } from '../hooks/use-enquiry';
import { useEnquiryModal } from '../hooks/use-enquiry-modal';
import { EnquiryForm } from './EnquiryForm';

const ENQUIRY_FOCUS_FIELDS = [
    {
        name: 'name',
        selector: 'input[name="name"]',
    },
    {
        name: 'email',
        selector: 'input[name="email"]',
    },
    {
        name: 'phoneNumber',
        selector: 'input[name="phoneNumber"]',
    },
    {
        name: 'postcode',
        selector: 'input[name="postcode"]',
    },
] as const;

const isEnquiryFieldValid = ({
    input,
    fieldName,
    value,
    requiresFullName,
}: {
    input: HTMLInputElement;
    fieldName: (typeof ENQUIRY_FOCUS_FIELDS)[number]['name'];
    value: string;
    requiresFullName: boolean;
}) => {
    switch (fieldName) {
        case 'name':
            return (
                requiresFullName
                    ? NAME_INPUT.safeParse(value)
                    : FIRST_NAME_INPUT.safeParse(value)
            ).success;
        case 'email':
            return EMAIL_INPUT.safeParse(value).success;
        case 'phoneNumber':
            return PHONE_NUMBER_INPUT.safeParse(value).success;
        case 'postcode':
            return !!value.trim() || input.dataset.enquiryFilled === 'true';
    }
};

const getInitialFocusTarget = ({
    container,
    requiresFullName,
}: {
    container: HTMLElement;
    requiresFullName: boolean;
}) => {
    for (const field of ENQUIRY_FOCUS_FIELDS) {
        const input = container.querySelector<HTMLInputElement>(field.selector);

        if (!input || input.disabled || input.readOnly) {
            continue;
        }

        const value = input.value.trim();
        if (!value) {
            return input;
        }

        if (
            !isEnquiryFieldValid({
                input,
                fieldName: field.name,
                value,
                requiresFullName,
            })
        ) {
            return input;
        }
    }

    const messageInput = container.querySelector<HTMLTextAreaElement>(
        'textarea[name="message"]',
    );

    if (messageInput && !messageInput.disabled && !messageInput.readOnly) {
        return messageInput;
    }

    return null;
};

/**
 * An modal with an enquiry form for users to submit an enquiry to a professional.
 * This component works with the `useEnquiryModal` hook to enable the modal on
 * all pages.
 *
 * There should only be one instance of this component near the root component.
 * This modal should be opened and closed via the `useEnquiryModal` hook.
 * @see {@link useEnquiryModal}
 */
export const EnquiryModal = () => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const { user, isLoggedIn } = useUserLoggedIn();
    const { isOpen, context, closeEnquiryModal } = useEnquiryModal();
    const { state } = useEnquirySession();
    const requiresFullName = !isLoggedIn || !!user.LastName;

    return isMobile ? (
        <Drawer
            open={isOpen && !!context}
            // Prevent messing with mobile keyboard input.
            // https://vaul.emilkowal.ski/inputs
            repositionInputs={false}
            onOpenChange={(open) => {
                if (!open) {
                    closeEnquiryModal();
                }
            }}
        >
            {context && (
                <DrawerContent
                    className="h-[calc(100dvh-0.5rem)] max-h-[calc(100dvh-0.5rem)] bg-gray-50"
                    variant="default"
                    hideDefaultCloseButton
                    noHandle
                    scrollable
                    leadingContent={
                        <DrawerSwipeZone onDismiss={closeEnquiryModal} />
                    }
                    onPointerDownOutside={(event) => {
                        if (state !== 'idle') {
                            event.preventDefault();
                        }
                    }}
                >
                    <DrawerHeader className="mb-4">
                        <div className="flex items-center gap-x-4">
                            <div className="flex shrink-0 items-center gap-x-2">
                                <div
                                    className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded"
                                    style={{
                                        backgroundColor:
                                            context.professional
                                                .LogoBackgroundColor,
                                    }}
                                >
                                    <img
                                        className="h-full w-full object-contain"
                                        src={context.professional.CompanyLogo}
                                        alt={context.professional.Title}
                                        loading="eager"
                                    />
                                </div>
                                {context.itemImage && (
                                    <div className="size-16 shrink-0 overflow-hidden rounded">
                                        <Image
                                            className="h-full w-full object-cover"
                                            src={context.itemImage}
                                            alt={context.itemTitle ?? ''}
                                            width={64}
                                            height={64}
                                            fit="cover"
                                            fadeIn={false}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0 grow text-left">
                                <DrawerTitle>
                                    {context.ctaLabel ??
                                        context.title ??
                                        'Make an Enquiry'}
                                </DrawerTitle>
                                {context.itemTitle && (
                                    <p className="mt-1 text-sm text-gray-500">
                                        {context.itemTitle}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Suppress missing description console warning */}
                        <DrawerDescription className="hidden" aria-hidden />
                    </DrawerHeader>
                    <EnquiryModalForm className="px-4 pb-8" />
                </DrawerContent>
            )}
        </Drawer>
    ) : (
        <Dialog
            open={isOpen && !!context}
            onOpenChange={(open) => {
                if (!open) {
                    closeEnquiryModal();
                }
            }}
        >
            {context && (
                <DialogContent
                    className="max-h-[95vh] w-[50rem] max-w-[95vw] overflow-y-auto bg-gray-50 p-4 transition-none md:p-12"
                    style={
                        {
                            /*
                             * Slide from bottom instead of the default top/centre pop.
                             * The enter/exit `transform` keyframe composes with the
                             * `translate-x-[-50%] translate-y-[-50%]` centring, which in
                             * Tailwind v4 lives on the separate `translate` property (v3
                             * put it on `transform`, so these offsets used to replace it).
                             * So the offsets are the v3 values minus the centring:
                             * x: -50% − (-50%) = 0 (stay centred, no left slide),
                             * y: 100% − (-50%) = 150% (slide up from below).
                             */
                            '--tw-enter-translate-x': '0',
                            '--tw-enter-translate-y': '150%',
                            '--tw-exit-translate-x': '0',
                            '--tw-exit-translate-y': '150%',
                            '--tw-enter-scale': '1',
                            '--tw-exit-scale': '1',
                            animationDuration: '500ms',
                            animationTimingFunction:
                                'cubic-bezier(0.16, 1, 0.3, 1)',
                        } as React.CSSProperties
                    }
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        const currentTarget = e.currentTarget;
                        if (!(currentTarget instanceof HTMLElement)) {
                            return;
                        }
                        window.setTimeout(() => {
                            if (!document.body.contains(currentTarget)) {
                                return;
                            }

                            getInitialFocusTarget({
                                container: currentTarget,
                                requiresFullName,
                            })?.focus({
                                preventScroll: true,
                            });
                        }, 0);
                    }}
                    onPointerDownOutside={(e) => {
                        // Prevent the modal from closing when the form is
                        // submitting and the user clicks outside the modal.
                        if (state !== 'idle') {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader className="text-left md:mb-8">
                        <div className="flex items-center gap-x-6 md:gap-x-8">
                            <div className="flex shrink-0 items-center gap-x-2">
                                <div
                                    className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded md:size-24"
                                    style={{
                                        backgroundColor:
                                            context.professional
                                                .LogoBackgroundColor,
                                    }}
                                >
                                    <img
                                        className="h-full w-full object-contain"
                                        src={context.professional.CompanyLogo}
                                        alt={context.professional.Title}
                                        loading="eager"
                                    />
                                </div>
                                {context.itemImage && (
                                    <div className="size-20 shrink-0 overflow-hidden rounded md:size-24">
                                        <Image
                                            className="h-full w-full object-cover"
                                            src={context.itemImage}
                                            alt={context.itemTitle ?? ''}
                                            width={100}
                                            height={100}
                                            fit="cover"
                                            fadeIn={false}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0 grow">
                                <DialogTitle>
                                    {context.ctaLabel ??
                                        context.title ??
                                        'Make an Enquiry'}
                                </DialogTitle>
                                {context.itemTitle && (
                                    <p className="mt-1 text-sm text-gray-500">
                                        {context.itemTitle}
                                    </p>
                                )}
                            </div>
                        </div>
                        <DialogDescription className="hidden" aria-hidden />
                    </DialogHeader>
                    <EnquiryModalForm />
                </DialogContent>
            )}
        </Dialog>
    );
};

const EnquiryModalForm = ({ className }: { className?: string }) => {
    const { context, closeEnquiryModal, openEnquiryModal } = useEnquiryModal();
    const { markSubmitted } = useEnquirySubmitted(context?.siteTreeID);

    const handleSuccess = useCallback(() => {
        markSubmitted();
    }, [markSubmitted]);

    if (!context) return null;

    return (
        <EnquiryForm
            className={className}
            professional={context.professional}
            itemType={context.itemType}
            siteTreeID={context.siteTreeID}
            source={context.source ?? 'EnquiryModal'}
            relatedItem={context.relatedItem}
            includeBranch={context.includeBranch}
            isModal
            showSuccessMessage={true}
            disableAutoFocus={true}
            itemTitle={context.messageSubject ?? context.itemTitle}
            ctaLabel={context.ctaLabel}
            onSuccess={handleSuccess}
            onFailure={closeEnquiryModal}
            onAuthCancel={openEnquiryModal}
            onSuccessClose={closeEnquiryModal}
        />
    );
};
